<script setup lang="ts">
import { computed, ref, defineProps } from "vue";
import { GroupTag, WeightRecord } from "@/types/v2";
import { useVaccinationTaskOperations } from "@/composables/tasks/vaccination/useVaccinationOperations";

// Simple date formatter function
const formatDate = (dateString: string | null): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return 'Invalid date';
  }
};

interface Props {
  records: WeightRecord[];
}

const props = defineProps<Props>();

const { getTagStyles } = useVaccinationTaskOperations();

const getGroupTags = (record: WeightRecord): GroupTag[] => {
  if (!record.group?.tags) return [];
  return record.group.tags.filter((tag: GroupTag) => tag.title !== "Start Tag");
};

const getGroupSortValue = (record: WeightRecord): string => {
  if (!record.group) return "";
  const tags = getGroupTags(record);
  if (!tags.length) return "Untagged";
  return tags.map((t) => t.title).join(", ");
};

// State for sorting
const sortField = ref<keyof WeightRecord>('timestamp');
const sortDirection = ref<'asc' | 'desc'>('desc');

// Sorted records
const sortedRecords = computed(() => {
  if (!props.records || !props.records.length) return [];

  return [...props.records].sort((a, b) => {
    if (sortField.value === 'timestamp') {
      const dateA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const dateB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return sortDirection.value === 'asc' ? dateA - dateB : dateB - dateA;
    }

    if (sortField.value === 'tank') {
      const tankNameA = a.tank ? a.tank.name ?? a.tank.label: '';
      const tankNameB = b.tank ? b.tank.name ?? b.tank.label : '';
      return sortDirection.value === 'asc'
          ? tankNameA.localeCompare(tankNameB)
          : tankNameB.localeCompare(tankNameA);
    }

    if (sortField.value === 'group') {
      const groupNameA = getGroupSortValue(a);
      const groupNameB = getGroupSortValue(b);
      return sortDirection.value === 'asc'
          ? groupNameA.localeCompare(groupNameB)
          : groupNameB.localeCompare(groupNameA);
    }

    if (sortField.value === 'weight') {
      return sortDirection.value === 'asc'
          ? a.weight - b.weight
          : b.weight - a.weight;
    }

    if (sortField.value === 'comment') {
      const commentA = a.comment || '';
      const commentB = b.comment || '';
      return sortDirection.value === 'asc'
          ? commentA.localeCompare(commentB)
          : commentB.localeCompare(commentA);
    }

    // Default case
    return 0;
  });
});

// Toggle sort direction
const toggleSort = (field: keyof WeightRecord) => {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortField.value = field;
    sortDirection.value = 'asc';
  }
};

// Get sort indicator
const getSortIndicator = (field: keyof WeightRecord) => {
  if (sortField.value !== field) return '';
  return sortDirection.value === 'asc' ? '▲' : '▼';
};
</script>

<template>
  <div class="overflow-x-auto">
    <div v-if="!records || records.length === 0" class="text-center py-8 text-gray-500">
      No weight records available
    </div>

    <table v-else class="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
      <thead class="bg-gray-50">
      <tr>
        <th
            @click="toggleSort('timestamp')"
            class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
        >
          Date {{ getSortIndicator('timestamp') }}
        </th>
        <th
            @click="toggleSort('tank')"
            class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
        >
          Tank {{ getSortIndicator('tank') }}
        </th>
        <th
            @click="toggleSort('group')"
            class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
        >
          Group {{ getSortIndicator('group') }}
        </th>
        <th
            @click="toggleSort('weight')"
            class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
        >
          Weight (g) {{ getSortIndicator('weight') }}
        </th>
        <th
            @click="toggleSort('comment')"
            class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
        >
          Comment {{ getSortIndicator('comment') }}
        </th>
      </tr>
      </thead>
      <tbody class="divide-y divide-gray-200">
      <tr v-for="(record, index) in sortedRecords" :key="index" class="hover:bg-gray-50">
        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {{ record.timestamp ? formatDate(record.timestamp) : 'N/A' }}
        </td>
        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {{ record.tank ? (record.tank.label || record.tank.name) : 'N/A' }}
        </td>
        <td class="px-6 py-4 text-sm">
          <div v-if="record.group" class="flex flex-wrap gap-1">
            <span
              v-for="tag in getGroupTags(record)"
              :key="tag.key"
              class="inline-block px-2 py-0.5 text-xs font-medium rounded-full"
              :style="getTagStyles(tag)"
            >
              {{ tag.title }}
            </span>
            <span
              v-if="getGroupTags(record).length === 0"
              class="inline-block px-2 py-0.5 text-xs font-medium rounded-full opacity-75 bg-gray-400 text-gray-700"
            >
              Untagged
            </span>
          </div>
          <span v-else class="text-gray-500">N/A</span>
        </td>
        <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
          {{ record.weight }}
        </td>
        <td class="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
          {{ record.comment || 'No comment' }}
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</template>
